export const PROVIDERS = [
  'facebook',
  'instagram',
  'tiktok',
  'gbp',
  'youtube',
  'linkedin',
  'x',
  'threads',
  'pinterest',
  'bluesky',
  'twitch',
  'web',
] as const

export type Provider = (typeof PROVIDERS)[number]

/**
 * The networks this build ships, in display order. Every UI surface iterates this list, and
 * the Connections screen derives its "not yet available" column from `PROVIDERS` minus this
 * one — so a network is connectable exactly when it appears here and has an adapter.
 *
 * Membership means "an adapter exists", not "the operator has registered an app": whether a
 * network can actually be connected today is `ProviderAdapter.isConfigured()`, and a network
 * with no credentials yet shows its setup copy rather than being hidden.
 */
export const LAUNCH_PROVIDERS = [
  'facebook',
  'instagram',
  'tiktok',
  'gbp',
  'youtube',
  'linkedin',
  'bluesky',
  'pinterest',
  'threads',
  'x',
] as const satisfies readonly Provider[]

export type LaunchProvider = (typeof LAUNCH_PROVIDERS)[number]

export interface ProviderMeta {
  id: Provider
  label: string
  /** Brand colour. Data only — never a chrome background. See docs/DESIGN_SYSTEM.md. */
  color: string
  /** Substitute colour when the brand colour is illegible on dark chrome. */
  colorOnDark?: string
  launched: boolean
  /** Metrics this network reports, in dashboard order. */
  metrics: readonly string[]
  /** Post kinds the planner can compose for this network. */
  postKinds: readonly string[]
  handlePrefix: string
}

export const PROVIDER_META: Record<Provider, ProviderMeta> = {
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    launched: true,
    metrics: ['followers', 'reach', 'impressions', 'engagement', 'posts', 'clicks'],
    postKinds: ['post', 'reel', 'story'],
    handlePrefix: '/',
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    color: '#E1306C',
    launched: true,
    metrics: ['followers', 'reach', 'impressions', 'engagement', 'saves', 'posts'],
    postKinds: ['post', 'reel', 'story', 'carousel'],
    handlePrefix: '@',
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    color: '#00C4BC',
    colorOnDark: '#00F2EA',
    launched: true,
    metrics: ['followers', 'views', 'engagement', 'shares', 'posts', 'watchTime'],
    postKinds: ['video'],
    handlePrefix: '@',
  },
  gbp: {
    id: 'gbp',
    label: 'Google Business',
    color: '#34A853',
    launched: true,
    metrics: ['views', 'searches', 'calls', 'directions', 'reviews', 'rating'],
    postKinds: ['update', 'offer', 'event'],
    handlePrefix: '',
  },
  youtube: {
    id: 'youtube',
    label: 'YouTube',
    color: '#FF0000',
    launched: true,
    metrics: ['subscribers', 'views', 'watchTime', 'engagement', 'videos', 'ctr'],
    postKinds: ['video', 'short'],
    handlePrefix: '@',
  },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    color: '#0A66C2',
    launched: true,
    metrics: ['followers', 'impressions', 'engagement', 'posts'],
    postKinds: ['post'],
    handlePrefix: '/',
  },
  /**
   * The mark is X's own near-black (`#0F1419`, the "Chirp" text colour its brand assets use for
   * the logo on light surfaces), with a near-white substitute on dark chrome — a true `#FFFFFF`
   * datum beside every other network's series is louder than any of them.
   *
   * `engagement` and `reach` are both deliberately absent. `shared/src/metrics.ts` defines
   * `engagement` as interactions over *reach*, and X measures no reach — it counts impressions,
   * and publishes no unique-viewer figure anywhere in v2. `saves` is listed because X's
   * `bookmark_count` is exactly the gesture Instagram and Pinterest call a save. There is no
   * `shares`: a repost never leaves X, so it travels under the extended name `reposts`
   * (`backend/src/providers/core/metricNames.ts`) rather than in a column that means "left the
   * network" everywhere else.
   *
   * `postKinds` is `['post']` alone. A **thread** — a chain of posts each replying to the last —
   * is a real X format and is not implemented: the adapter publishes one post per call and the
   * composer has no multi-part body to build one from. Listing it would offer a kind the publish
   * path would have to refuse. See `backend/src/providers/x/publish.ts`.
   */
  x: {
    id: 'x',
    label: 'X',
    color: '#0F1419',
    colorOnDark: '#E7E9EC',
    launched: true,
    metrics: ['followers', 'impressions', 'saves', 'posts'],
    postKinds: ['post'],
    handlePrefix: '@',
  },
  /**
   * The mark is **monochrome and has no brand hue**. Meta's own Threads brand resource publishes
   * clear-space and usage rules and no colour table at all; the icon is black on light surfaces
   * and white on dark ones, and altering its colour is explicitly prohibited. So `color` is a
   * literal black rather than an invented brand tone, and `colorOnDark` is the near-white this
   * palette already uses for X — a true `#FFFFFF` datum on dark chrome is louder than every other
   * network's series beside it.
   *
   * `engagement` is deliberately **not** listed. `shared/src/metrics.ts` defines it as
   * interactions over *reach*, and Threads measures no reach — it reports `views`, never a unique
   * -viewer figure. `impressions` is absent for the same reason in reverse: it would be `views`
   * under a second name. `shares` is listed because Threads reports it per post, and it means what
   * it means everywhere else here — the post leaving the network. A Threads **repost** is a
   * different number and travels under the extended name `reposts`
   * (`backend/src/providers/core/metricNames.ts`). See `backend/src/providers/threads/map.ts`.
   */
  threads: {
    id: 'threads',
    label: 'Threads',
    color: '#000000',
    colorOnDark: '#E7E9EC',
    launched: true,
    metrics: ['followers', 'views', 'shares', 'posts'],
    postKinds: ['post', 'carousel'],
    handlePrefix: '@',
  },
  /**
   * `reach` and `engagement` are both deliberately absent.
   *
   * Pinterest counts impressions, not unique viewers, so there is no reach to report. And
   * `shared/src/metrics.ts` defines `engagement` as interactions over *reach* — Pinterest's own
   * `ENGAGEMENT_RATE` is over impressions, which is a different quantity wearing the same word.
   * Its `ENGAGEMENT` **count** is reported, under the extended name `interactions`. See
   * `backend/src/providers/pinterest/map.ts`.
   */
  pinterest: {
    id: 'pinterest',
    label: 'Pinterest',
    color: '#E60023',
    launched: true,
    metrics: ['followers', 'impressions', 'saves', 'clicks', 'posts'],
    postKinds: ['pin'],
    handlePrefix: '/',
  },
  /**
   * `engagement` is deliberately not listed. `shared/src/metrics.ts` defines it as interactions
   * over *reach*, and Bluesky measures no reach — it has no analytics API at all, only public
   * interaction counters. See `backend/src/providers/bluesky/map.ts`.
   */
  bluesky: {
    id: 'bluesky',
    label: 'Bluesky',
    color: '#0085FF',
    launched: true,
    metrics: ['followers', 'posts', 'shares'],
    postKinds: ['post'],
    handlePrefix: '@',
  },
  twitch: {
    id: 'twitch',
    label: 'Twitch',
    color: '#9146FF',
    launched: false,
    metrics: ['followers', 'views', 'subscriptions'],
    postKinds: [],
    handlePrefix: '/',
  },
  web: {
    id: 'web',
    label: 'Website',
    color: '#5C636E',
    launched: false,
    metrics: ['sessions', 'users', 'pageviews'],
    postKinds: [],
    handlePrefix: '',
  },
}

/**
 * How many characters of caption each network will accept.
 *
 * This lives in `shared` because two unrelated places have to agree on it: the composer counts a
 * draft down against it (`frontend/src/features/planner/limits.ts`, which reads this table and
 * documents *why* each number is what it is, network by network), and the AI writing assistant
 * tells the model the ceiling before it writes (`backend/src/modules/ai/prompt.ts`). A second
 * copy of these numbers would drift, and the drift would show up as an assistant confidently
 * writing 600 characters for a network that takes 500.
 *
 * Two of them are not literally character counts and the composer's own notes say so — Bluesky
 * publishes 300 *graphemes* and Threads 500 *characters* — but UTF-16 length is never smaller
 * than a grapheme count, so counting with `.length` errs toward refusing a post the network
 * would have taken rather than waving through one it will reject. That is the safe direction
 * for a counter and for a prompt alike.
 *
 * X's 280 is the same kind of conservative floor. `POST /2/tweets` declares **no** text limit in
 * its schema at all, and X's help pages put the real ceiling on the *authenticated account's*
 * Premium status rather than on the API plan — a subscriber gets far more. Nothing in the API
 * reports which, so the standard-account number is used and a Premium customer simply cannot
 * spend their whole allowance from here.
 */
export const CAPTION_LIMITS: Record<Provider, number> = {
  facebook: 63_206,
  instagram: 2_200,
  tiktok: 2_200,
  gbp: 1_500,
  youtube: 5_000,
  linkedin: 3_000,
  x: 280,
  threads: 500,
  pinterest: 800,
  bluesky: 300,
  twitch: 500,
  web: 10_000,
}

/**
 * `ProviderMeta` as `GET /api/v1/meta/providers` actually returns it.
 *
 * The two extra fields are what `docs/INGESTION.md` requires the connect screen to read: a
 * network whose app the operator has not registered reports `configurable: false`, and its
 * connect button explains what has to be registered instead of failing at the redirect. The
 * fields are optional so the base `ProviderMeta` — used everywhere a colour or a label is
 * needed — stays a pure static table with no server state in it.
 */
export interface ProviderMetaWire extends ProviderMeta {
  /** An adapter exists in this build (`implemented`) **and** credentials are present. */
  configurable?: boolean
  /** True when an adapter exists at all, whether or not it is configured. */
  implemented?: boolean
  /** The OAuth scopes the redirect will request, listed to the user beforehand. */
  scopes?: readonly string[]
}

export const launchProviderMeta = (): ProviderMeta[] =>
  LAUNCH_PROVIDERS.map((id) => PROVIDER_META[id])

export const isLaunchProvider = (p: Provider): p is LaunchProvider =>
  (LAUNCH_PROVIDERS as readonly Provider[]).includes(p)

/** Network colour, corrected for the surface it sits on. */
export const providerColor = (p: Provider, on: 'paper' | 'chrome' = 'paper'): string => {
  const meta = PROVIDER_META[p]
  return on === 'chrome' ? (meta.colorOnDark ?? meta.color) : meta.color
}
